import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Spinner,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { usePortfolio } from "../hooks/usePortfolio";
import { useSedes } from "../hooks/useSedes";

const INTERRUPTED_STATUSES = ["INTERRUPTED", "EXPIRED", "DEPLETED"];

const STATUS_LABELS = {
  INTERRUPTED: { label: "Interrumpido", bg: "danger" },
  EXPIRED: { label: "Vencido", bg: "warning" },
  DEPLETED: { label: "Agotado", bg: "secondary" },
};

function CarteraPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [sedeFilter, setSedeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const { data: cycles, isLoading, error } = usePortfolio();
  const { data: sedes } = useSedes();

  // Filter to only overdue/interrupted cycles
  const vencidos = useMemo(
    () =>
      (cycles ?? []).filter((c) =>
        INTERRUPTED_STATUSES.includes(c.status) ||
        INTERRUPTED_STATUSES.includes(c.subscription?.status)
      ),
    [cycles]
  );

  // Options come from the academy's sedes (headquarters), not from the (often
  // empty) overdue rows — so the filter always lists the real sedes.
  const sedeOptions = useMemo(
    () =>
      (sedes ?? []).map((s) => ({
        value: s.id,
        label: s.reference_name || s.address || `Sede ${s.id}`,
      })),
    [sedes]
  );

  const statusOptions = INTERRUPTED_STATUSES.map((s) => ({
    value: s,
    label: STATUS_LABELS[s]?.label ?? s,
  }));

  const filtered = useMemo(() => {
    let result = vencidos;
    if (sedeFilter) {
      result = result.filter(
        (c) => c.headquarters?.id === sedeFilter.value
      );
    }
    if (statusFilter) {
      result = result.filter(
        (c) =>
          c.status === statusFilter.value ||
          c.subscription?.status === statusFilter.value
      );
    }
    return result;
  }, [vencidos, sedeFilter, statusFilter]);

  const handleRegisterPago = (cycle) => {
    const studentId =
      cycle.student?.id ?? cycle.student;
    navigate(`/${slug}/app/academia/dinero/pagos?student=${studentId}`);
  };

  const effectiveStatus = (cycle) =>
    INTERRUPTED_STATUSES.find(
      (s) => cycle.status === s || cycle.subscription?.status === s
    ) ?? cycle.status;

  return (
    <>
      <div className="container-fluid px-3">
        <div className="py-3">
          <h1 className="h4 mb-0">Retención</h1>
          <p className="text-muted small mb-0">
            Estudiantes inactivos: ciclo de estudio interrumpido por falta de
            clases recientes
          </p>
        </div>

        {/* Filters */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-4">
            <Select
              options={sedeOptions}
              value={sedeFilter}
              onChange={setSedeFilter}
              isClearable
              placeholder="Filtrar por sede"
              classNamePrefix="react-select"
            />
          </div>
          <div className="col-12 col-md-4">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              isClearable
              placeholder="Filtrar por estado"
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {isLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        )}

        {error && (
          <Alert variant="danger">No se pudo cargar la cartera vencida.</Alert>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <Card>
            <Card.Body className="text-center py-5 text-muted">
              No hay estudiantes con cartera vencida
              {sedeFilter || statusFilter ? " con los filtros actuales" : ""}.
            </Card.Body>
          </Card>
        )}

        {/* Desktop table — hidden on mobile */}
        {!isLoading && filtered.length > 0 && (
          <>
            <div className="d-none d-sm-block">
              <Card>
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Estudiante</th>
                        <th>Último ciclo</th>
                        <th>Interrupción</th>
                        <th>Saldo h.</th>
                        <th>Estado</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((cycle, i) => {
                        const st = effectiveStatus(cycle);
                        const badge = STATUS_LABELS[st] ?? { label: st, bg: "secondary" };
                        return (
                          <tr key={i} data-testid="portfolio-row">
                            <td>
                              <div className="fw-medium">
                                {cycle.student?.full_name ?? cycle.student?.email ?? "—"}
                              </div>
                              <div className="text-muted small">
                                {cycle.student?.email}
                              </div>
                            </td>
                            <td>
                              Ciclo {cycle.cycle_number} · {cycle.months ?? 0} mes(es)
                            </td>
                            <td>
                              {cycle.last_class
                                ? dayjs(cycle.last_class).format("D MMM YYYY")
                                : "—"}
                            </td>
                            <td>
                              {cycle.hours_balance != null
                                ? `${cycle.hours_balance} h`
                                : "—"}
                            </td>
                            <td>
                              <Badge bg={badge.bg}>{badge.label}</Badge>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                style={{ minHeight: 44 }}
                                onClick={() => handleRegisterPago(cycle)}
                                data-testid="cta-registrar-pago"
                              >
                                Registrar pago
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </div>

            {/* Mobile stacked cards — hidden on sm+ */}
            <div className="d-sm-none d-flex flex-column gap-3">
              {filtered.map((cycle, i) => {
                const st = effectiveStatus(cycle);
                const badge = STATUS_LABELS[st] ?? { label: st, bg: "secondary" };
                return (
                  <Card key={i} data-testid="portfolio-card">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="fw-medium">
                            {cycle.student?.full_name ?? cycle.student?.email ?? "—"}
                          </div>
                          <div className="text-muted small">
                            {cycle.student?.email}
                          </div>
                        </div>
                        <Badge bg={badge.bg}>{badge.label}</Badge>
                      </div>
                      <div className="text-muted small mb-1">
                        Ciclo {cycle.cycle_number} · {cycle.months ?? 0} mes(es)
                        {cycle.last_class
                          ? ` · última clase ${dayjs(cycle.last_class).format("D MMM")}`
                          : ""}
                      </div>
                      {cycle.hours_balance != null && (
                        <div className="text-muted small mb-2">
                          Saldo: {cycle.hours_balance} h
                        </div>
                      )}
                      <Button
                        variant="primary"
                        className="w-100"
                        style={{ minHeight: 44 }}
                        onClick={() => handleRegisterPago(cycle)}
                        data-testid="cta-registrar-pago"
                      >
                        Registrar pago
                      </Button>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export { CarteraPage };
